import React, { useEffect, useState, useCallback } from "react";

import dynamic from "next/dynamic";

import { useEnableRestrictSonderSignup } from "components/hooks/feature-flags";
import { useTranslation } from "components/i18n/source";
import { useAnalyticsTracker } from "components/instrumentation/source/analytics";
import { useAppContext, useCookies } from "components/store/source";
import {
  Link,
  Loader,
  Modal,
  Text,
  setExitIntentViewed,
} from "components/ui/source";
import { LoadingLayout } from "components/ui/source/layouts";

import { useNavbarRendered } from "../Navbar/Navbar";

import LoginForm from "./LoginForm";
import SignUpEmailContainer from "./SignUpEmailContainer";
import SignUpSelectorContainer from "./SignUpSelectorContainer";
import { useFlow } from "./utils";

// analytics.track causes rerender.
function SignUpPopupEvent({ signUpPopupIsOpen, signUpPopupProps }) {
  const { analytics } = useAnalyticsTracker();

  useEffect(() => {
    if (signUpPopupIsOpen) {
      analytics.track("Sign Up Page Load", {
        source: signUpPopupProps.source,
      });
    }
  }, [signUpPopupIsOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

// The SignUpReferralContainer is only used on the /referral page, so there's no
// need to load all of that code on every page.
const SignUpReferralContainer = dynamic(
  () => import("./SignUpReferralContainer"),
  { loading: () => <Loader /> },
);

import styles from "./Accounts.module.scss";

function SignUpPopup() {
  const { t } = useTranslation();
  const { signUpPopupIsOpen, closeSignUpPopup, signUpPopupProps, allowSignUp } =
    useAppContext();
  const flow = useFlow();

  const enableRestrictSonderSignup = useEnableRestrictSonderSignup();

  const showSignUpModal = enableRestrictSonderSignup
    ? allowSignUp(signUpPopupProps?.source)
    : true;

  const { analytics } = useAnalyticsTracker();
  const { setCookie, destroyCookie } = useCookies();
  const [isLoading, setLoading] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const isNavbarRendered = useNavbarRendered();

  const initialShowSelector = signUpPopupProps?.showSingleSignOnPage ?? true;
  const [showSelector, setShowSelector] = useState(initialShowSelector);
  useEffect(() => {
    setShowSelector(initialShowSelector);
  }, [initialShowSelector]);

  const initialEmail = signUpPopupProps?.initialEmail ?? "";
  const [email, setEmail] = useState(initialEmail);
  useEffect(() => {
    setEmail(initialEmail);
  }, [initialEmail]);

  const prefix = "modals.accounts.signUp";

  function setExitModalViewed() {
    // Set exit modal viewed
    if (flow.showExitIntent) {
      setExitIntentViewed(setCookie, destroyCookie);
    }
  }

  async function sendSubmitLog(method, email) {
    try {
      await analytics.track("Signed Up", {
        method,
        source: signUpPopupProps.source,
        email,
      });
    } catch {
      // do nothing
    }
    setExitModalViewed();
    await sendLog("submitted", method);
  }

  const sendLog = useCallback(
    async (status = "triggered", source = null) => {
      if (!flow.showExitIntent) return;

      // Clean up unnecessary payload and add source to eventPayload
      const eventPayload = Object.assign({}, signUpPopupProps, { status });
      if (source) eventPayload.source = source;
      delete eventPayload.isExitIntentModal;

      try {
        await analytics.track("Checkout Exit Modal ", eventPayload);
      } catch {
        // do nothing
      }
    },
    [flow.showExitIntent, signUpPopupProps, analytics],
  );

  function closePopup() {
    setExitModalViewed();
    closeSignUpPopup();
  }

  function submitEmail(email) {
    setEmail(email);
    setShowSelector(false);
  }

  function switchLogin(email) {
    setEmail(email);
    setShowLogin(true);
  }

  const modalProps = {
    isOpen: signUpPopupIsOpen,
    onRequestClose: closePopup,
    shouldCloseOnOverlayClick: !isLoading,
    shouldCloseOnEsc: !isLoading,
    className: styles.modal,
  };

  // Open logging
  useEffect(() => {
    if (!signUpPopupIsOpen) return;
    sendLog();
  }, [signUpPopupIsOpen, sendLog]);

  return showSignUpModal && isNavbarRendered ? (
    <Modal modalProps={modalProps}>
      <SignUpPopupEvent
        signUpPopupIsOpen={signUpPopupIsOpen}
        signUpPopupProps={signUpPopupProps}
      />
      <LoadingLayout loading={isLoading}>
        <Modal.Close onClick={closePopup} shouldClose={!isLoading} />
        {!showLogin ? (
          <>
            {showSelector ? (
              <SignUpSelectorContainer
                setLoading={setLoading}
                setEmail={submitEmail}
                logSubmit={sendSubmitLog}
                showLogin={switchLogin}
              />
            ) : flow.showReferral ? (
              <SignUpReferralContainer setLoading={setLoading} email={email} />
            ) : (
              <SignUpEmailContainer
                setLoading={setLoading}
                logSubmit={sendSubmitLog}
                email={email}
              />
            )}

            <Text className={styles.centered_text} types={["subtitles"]}>
              {t(`${prefix}.toc.disclaimer`)}&nbsp;
              <Link
                href="/privacy-policy"
                translateKey={`${prefix}.toc.privacyPolicy`}
                underline
                textElement="span"
                newTab
              />
              &nbsp;{t(`${prefix}.toc.and`)}
              <br />
              <Link
                href="/terms-of-service"
                translateKey={`${prefix}.toc.termsOfService`}
                underline
                textElement="span"
                newTab
              />
            </Text>
          </>
        ) : (
          <LoginForm
            openSignUpPopup={() => setShowLogin(false)}
            setLoading={setLoading}
            logSubmit={sendSubmitLog}
            closePopup={closePopup}
            initialEmail={email}
            fromSignUp
          />
        )}
      </LoadingLayout>
    </Modal>
  ) : null;
}

export default SignUpPopup;
